import { useEffect, useRef, useState } from 'react';

export function useSwipeable({
  onDelete,
  onSelected,
}: {
  onDelete: () => void;
  onSelected: () => void;
}) {
  const listElementRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const backgroundRef = useRef<HTMLDivElement | null>(null);

  const [stateConfirmDeleteDialog, setStateConfirmDeleteDialog] =
    useState<boolean>(false);

  const dragStartXRef = useRef(0);
  const leftRef = useRef(0);
  const draggedRef = useRef(false);

  useEffect(() => {
    window.addEventListener('mouseup', onDragEndMouse);
    window.addEventListener('touchend', onDragEndTouch);
    return () => {
      window.removeEventListener('mouseup', onDragEndMouse);
      window.removeEventListener('touchend', onDragEndTouch);
    };
  }, []);

  function onDragStartMouse(evt: React.MouseEvent<HTMLDivElement>) {
    onDragStart(evt.clientX);
    window.addEventListener('mousemove', onMouseMove);
  }

  function onDragStartTouch(evt: React.TouchEvent<HTMLDivElement>) {
    const touch = evt.targetTouches[0];
    onDragStart(touch.clientX);
    window.addEventListener('touchmove', onTouchMove);
  }

  function onDragStart(clientX: number) {
    draggedRef.current = true;
    dragStartXRef.current = clientX;
    if (listElementRef.current) {
      listElementRef.current.className = 'SwipeableListItem';
    }

    requestAnimationFrame(updatePosition);
  }

  function updatePosition() {
    if (draggedRef.current && listElementRef.current) {
      requestAnimationFrame(updatePosition);
      listElementRef.current.style.transform = `translateX(${leftRef.current}px)`;
    }
  }

  function onMouseMove(evt: MouseEvent) {
    const left = evt.clientX - dragStartXRef.current;
    if (left < 0) {
      leftRef.current = left;
    }
  }

  function onTouchMove(evt: TouchEvent) {
    const touch = evt.targetTouches[0];
    const left = touch.clientX - dragStartXRef.current;
    if (left < 0) {
      leftRef.current = left;
    }
  }

  function onDragEndMouse() {
    window.removeEventListener('mousemove', onMouseMove);
    onDragEnd();
  }

  function onDragEndTouch() {
    window.removeEventListener('touchmove', onTouchMove);
    onDragEnd();
  }

  function onDragEnd() {
    if (draggedRef.current && listElementRef.current && wrapperRef.current) {
      draggedRef.current = false;
      const threshold = 0.5;

      if (
        leftRef.current <
        listElementRef.current.offsetWidth * threshold * -1
      ) {
        leftRef.current = -listElementRef.current.offsetWidth * 2;

        try {
          onSelected();
          setStateConfirmDeleteDialog(true);
          wrapperRef.current.style.maxHeight = '0';
        } catch (error) {
          leftRef.current = 0;
        }
      } else {
        leftRef.current = 0;
      }

      listElementRef.current.className = 'BouncingListItem';
      listElementRef.current.style.transform = `translateX(${leftRef.current}px)`;
    }
  }

  async function handleDelete() {
    await onDelete();
    setStateConfirmDeleteDialog(false);
  }

  function handleCancelDelete() {
    if (listElementRef.current && wrapperRef.current) {
      leftRef.current = 0;
      wrapperRef.current.style.maxHeight = 'unset';
      listElementRef.current.className = 'BouncingListItem';
      listElementRef.current.style.transform = `unset`;
    }
    setStateConfirmDeleteDialog(false);
  }

  return {
    listElementRef,
    wrapperRef,
    backgroundRef,
    onDragStartMouse,
    onDragStartTouch,
    stateConfirmDeleteDialog,
    handleCancelDelete,
    handleDelete,
  };
}
